import { useState, useEffect } from 'react';
import router from 'next/router';
import { Add, Delete, Flag } from '@mui/icons-material';
import { MenuItem, IconButton } from '@mui/material';
import { useSnackbar } from 'notistack';
import { PostWithMatchPostId, Stats } from 'lib/types';
import { useApi, useCurrentUser } from 'lib/contexts/ApplicationState';
import { OGPostCard } from 'components/PostCard/OGPostCard';
import { PostStats } from 'components/PostStats';
import { DiffPostStats } from 'components/PostStats/PostStats';
import { Credit } from 'lib/types/credits';
import { userIsAdmin } from 'lib/helpers';
import { ConfirmationModal } from 'components/ConfirmationModal';
import { useModal } from 'mui-modal-provider';
import { LoadingOverlay } from 'components/LoadingOverlay';

interface Props {
  matchId: string;
  matchPosts: PostWithMatchPostId[];
  matchMetrics: Stats;
}

export const Matches = ({
  matchId,
  matchPosts,
  matchMetrics,
}: Props) => {
  const API = useApi();
  const currentUser = useCurrentUser();
  const isAdmin = userIsAdmin(currentUser);
  const [redirecting, setRedirecting] = useState(false);
  const [posts, setPosts] = useState<PostWithMatchPostId[]>(matchPosts);
  const [flagged, setFlagged] = useState((matchMetrics as any).needsReview);
  const [statDiff, setStatDiff] = useState<Stats | undefined>();
  const { enqueueSnackbar } = useSnackbar();
  const { showModal } = useModal();
  const [isSaving, setIsSaving] = useState(false);

  const isUserCredited = (p: PostWithMatchPostId) => p.credits?.find(
    (cr: Credit) => cr.userId === currentUser?.id,
  );

  const addToMatches = () => {
    const profileUrl = `/${currentUser?.username}`;
    router.push({
      pathname: profileUrl,
      query: { st: matchId },
    });
  };

  const removeFromMatches = async (matchPostId: string) => {
    try {
      const result = await API.deleteMatchPost(matchPostId);
      if (!result) {
        enqueueSnackbar('Match successfully deleted.', { variant: 'success' });
        setRedirecting(true);
        router.back();
      } else {
        enqueueSnackbar('Post successfully removed from match', { variant: 'success' });
        setPosts(posts.filter((p) => p.matchPostId !== matchPostId));
      }
    } catch (e) {
      enqueueSnackbar('There was an error.  Please try again later.', { variant: 'error' });
    }
  };

  const onClick = () => {
    const modal = showModal(ConfirmationModal, {
      text: 'By removing the flag, you are are indicating that you have reviewed the posts and they all belong to this match. Are you sure?',
      onNo: () => {
        modal.destroy();
      },
      onYes: () => {
        unFlagMatch();
        modal.destroy();
      },
    });
  };

  const unFlagMatch = async () => {
    try {
      await API.unflagMatch(matchId);
      setFlagged(false);
      enqueueSnackbar('Match successfully unflagged.', { variant: 'success' });
    } catch (e) {
      enqueueSnackbar('Unable to unflag match.', { variant: 'error' });
    }
  };

  const statDifference = () => {
    if (!currentUser || !statDiff) {
      return null;
    }
    if (statDiff.totalPosts < 1) {
      return null;
    }
    return (
      <div className="border-orange border p-4 mb-4 rounded-md text-center">
        <span>
          You have credits on
          {' '}
          <span className="font-semibold">
            {matchMetrics.totalPosts - statDiff.totalPosts}
          </span>
          {' '}
          of the
          {' '}
          <span className="font-semibold">
            {matchMetrics.totalPosts}
          </span>
          {' '}
          posts in this match. Add credits on the other
          {' '}
          <span className="font-semibold">
            {statDiff.totalPosts}
          </span>
          {' '}
          post(s) to claim:
          <DiffPostStats stats={statDiff} />
        </span>
      </div>
    );
  };

  const getStatDifference = async () => {
    const diff = await API.getMatchStatsDifference(matchId);
    setStatDiff(diff);
  };

  const copyCredits = async () => {
    setIsSaving(true);
    try {
      await API.copyOriginalPostCreditsToAll(matchId, posts[0].id);
      router.reload();
    } catch (error: any) {
      enqueueSnackbar(`There was an error copying the credit(s): ${error.message}`, { variant: 'error' });
    }
    setIsSaving(false);
  };

  useEffect(() => {
    if (currentUser) getStatDifference();
  }, [currentUser]);

  if (redirecting) return <div className="text-xl font-semibold text-center mt-8">Redirecting to previous page...</div>;
  return (
    <div className="max-w-screen-xl m-auto p-6">
      {statDifference()}
      <div className="md:grid md:grid-cols-4 md:gap-6">
        <div className="col-span-1 pb-4">
          <h1 className="text-xl font-semibold pb-4">Oldest/Original Post</h1>
          <OGPostCard
            post={posts[0]}
            includeMetrics
            includeCreditPreview
            showFavorite
            context="match"
          />
          {isSaving && <LoadingOverlay open text="Copying credits from original..." />}
          <button type="button" onClick={copyCredits} className="btn-primary mt-4" disabled={isSaving}>Copy credits to all matches</button>
        </div>
        <div className="col-span-3">
          <div className="flex flex-wrap justify-between">
            <h2 className="text-xl font-semibold mb-4">
              Matching Posts
              {(isAdmin && flagged) && (
                <IconButton
                  color="error"
                  title="Unflag"
                  aria-label="Unflag"
                  onClick={onClick}
                  size="small"
                >
                  <Flag color="error" />
                </IconButton>

              )}
            </h2>
            {matchMetrics && <PostStats stats={matchMetrics} />}
          </div>
          <div className="md:grid md:grid-cols-4 gap-x-4">
            {posts.slice(1).map((p) => {
              const canRemove = isUserCredited(p) || isAdmin;
              return (
                <div className="relative mb-4" key={p.id}>
                  {canRemove && (
                    <button
                      type="button"
                      onClick={() => removeFromMatches(p.matchPostId)}
                      className="bg-white hover:bg-orange right-2 top-12 absolute rounded-full p-0.5 z-10"
                    >
                      <Delete />
                    </button>
                  )}
                  <OGPostCard
                    key={p.id}
                    post={p}
                    includeMetrics
                    includeCreditPreview
                    moreMenu={canRemove && (
                      <MenuItem
                        onClick={() => removeFromMatches(p.matchPostId)}
                      >
                        <Delete fontSize="small" className="mr-1" />
                        {' '}
                        Remove from set
                      </MenuItem>
                    )}
                    showFavorite
                    context="match"
                  />
                </div>
              );
            })}
            <div className="pb-4">
              <button
                type="button"
                onClick={addToMatches}
                className="text-lightgray border-lightgray border-2 border-dashed rounded-md flex items-center justify-center h-full min-h-[300px] w-full cursor-pointer"
              >
                <Add fontSize="large" color="inherit" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
